import React from 'react';
import { pick } from 'lodash';
import styled from 'styled-components/macro';
import scaleSize from 'utils/scale-size';
import { getImageUrlWithResizer } from 'utils/image';
import { LIST_ITEMS_SIZE } from 'config';
import { CONTENT_TYPES } from 'constants/content-types';
import { Focusable } from 'components/navigation';
import PlayIcon from 'components/Icons/item-play';
import ProgramListItem from './ProgramListItem';

export const LIST_ITEM_MARGIN = scaleSize(19);

type ItemSizeType = { width: number; height: number; metadataHeight: number };

interface IListItemCover {
  url: string;
  itemSize: ItemSizeType;
}

const ListItemCover = styled.div.attrs<IListItemCover>((props) => {
  return {
    style: {
      backgroundImage: `url(${props.url})`,
    },
  };
})<IListItemCover>`
  width: ${({ itemSize }) => scaleSize(itemSize.width)}px;
  height: ${({ itemSize }) => scaleSize(itemSize.height)}px;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.imagePlaceholder};
  border-radius: ${scaleSize(12)}px;
  transform: translate3d(0, 0, 0);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
`;

const ListItemTitle = styled.span<{ itemSize: ItemSizeType }>`
  font-family: RobotoCondensedBold, sans-serif;
  font-size: ${scaleSize(34)}px;
  font-weight: bold;
  letter-spacing: ${scaleSize(-0.24)}px;
  height: ${scaleSize(64)}px;
  line-height: ${scaleSize(64)}px;
  width: ${({ itemSize }) => scaleSize(itemSize.width - 8)}px;
  text-overflow: ellipsis;
  overflow: hidden;
  margin-bottom: ${scaleSize(5)}px;
  color: ${({ theme }) => theme.mainTextColor};
  transform: translate3d(0, 0, 0);
  display: block;
  white-space: nowrap;
`;

interface IListItemWrapper {
  transformOnFocus?: boolean;
  itemSize: ItemSizeType;
  changeBackgoundOnFocus?: boolean;
}

const ListItemWrapper = styled(Focusable)<IListItemWrapper>`
  width: ${({ itemSize }) => scaleSize(itemSize.width)}px;
  height: ${({ itemSize }) => scaleSize(itemSize.height + itemSize.metadataHeight)}px;
  display: inline-block;
  text-align: center;
  margin-right: ${scaleSize(LIST_ITEM_MARGIN)}px;
  box-sizing: border-box;
  cursor: pointer;
  vertical-align: top;
  position: relative;

  &:focus {
    background-color: ${({ theme, changeBackgoundOnFocus }) =>
      changeBackgoundOnFocus && theme.activeColor};
    & > ${ListItemCover} {
      transform: translate3d(
        0,
        -${({ transformOnFocus }) => (transformOnFocus ? scaleSize(20) : 0)}px,
        0
      );

      &:after {
        content: '';
        display: block;
        width: ${({ itemSize }) => scaleSize(itemSize.width - 15)}px;
        height: ${({ itemSize }) => scaleSize(itemSize.height - 15)}px;
        position: absolute;
        border: solid ${scaleSize(8)}px ${({ theme }) => theme.activeColor};
        border-radius: ${scaleSize(12)}px;
      }
    }

    & > ${ListItemTitle} {
      font-size: ${scaleSize(48)}px;
      color: ${({ theme }) => theme.activeColor};
      letter-spacing: ${scaleSize(-0.33)}px;
    }
  }
`;

const StyledPlayIcon = styled(PlayIcon)<{ isActive: boolean }>`
  position: absolute;
  bottom: ${scaleSize(20)}px;
  right: ${scaleSize(20)}px;
  .fill-change {
    fill: ${({ isActive, theme }) => (isActive ? theme.activeColor : theme.iconDefaultColor)};
  }
`;

interface IListItem {
  id: string;
  index: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: any;
  transformOnFocus?: boolean;
  changeBackgoundOnFocus?: boolean;
  showPlayIcon?: boolean;
  isActive?: boolean;
  onFocus: (index: number) => void;
  onEnter: (payload: number) => void;
  className?: string;
  contentType: string;
}

type StaticComponents = {
  BANNER: React.FC<IListItem>;
  CHANNEL: React.FC<IListItem>;
  VOD_ASSET: React.FC<IListItem>;
  EPISODE: React.FC<IListItem>;
  TV_SERIES: React.FC<IListItem>;
  PROFILE: React.FC<IListItem>;
  PROGRAM: React.FC<IListItem>;
};

const ListItem: React.FC<IListItem> & StaticComponents = (props) => {
  const { id, index, item, transformOnFocus, changeBackgoundOnFocus, showPlayIcon } = props;
  const { isActive = false, onFocus, onEnter, className, contentType } = props;
  const itemSize = LIST_ITEMS_SIZE[contentType] || LIST_ITEMS_SIZE.LARGE;

  const imgUrl = getImageUrlWithResizer(item, pick(itemSize, ['width', 'height']));

  return (
    <ListItemWrapper
      key={id}
      id={id}
      className={className}
      onFocus={onFocus}
      onEnter={onEnter}
      // TODO: Focus and onEnter receivers different params
      payload={index}
      index={index}
      itemSize={itemSize}
      transformOnFocus={transformOnFocus}
      changeBackgoundOnFocus={changeBackgoundOnFocus}
    >
      <ListItemCover itemSize={itemSize} url={imgUrl}>
        {showPlayIcon && <StyledPlayIcon isActive={isActive} />}
      </ListItemCover>
      <ListItemTitle itemSize={itemSize}>{item.name}</ListItemTitle>
    </ListItemWrapper>
  );
};

ListItem.BANNER = (props) => {
  return <ListItem contentType={CONTENT_TYPES.BANNER} {...props} />;
};

ListItem.CHANNEL = (props) => {
  return <ListItem contentType={CONTENT_TYPES.CHANNEL} transformOnFocus {...props} />;
};

ListItem.VOD_ASSET = (props) => {
  return <ListItem contentType={CONTENT_TYPES.VOD_ASSET} transformOnFocus {...props} />;
};

ListItem.EPISODE = (props) => {
  return <ListItem contentType={CONTENT_TYPES.EPISODE} showPlayIcon transformOnFocus {...props} />;
};

ListItem.TV_SERIES = (props) => {
  return <ListItem contentType={'SMALL'} transformOnFocus {...props} />;
};

ListItem.PROFILE = (props) => {
  return <ListItem contentType={'MEDIUM'} transformOnFocus changeBackgoundOnFocus {...props} />;
};

ListItem.PROGRAM = (props) => <ProgramListItem {...props} />;

export default ListItem;
